import { ZfPill } from '@zafire/react';
import type { ColDef, ICellRendererParams, IDetailCellRendererParams } from 'ag-grid-enterprise';
import moment from 'moment/moment';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddGenericDocuments, useClientPackages, useDeleteAppendixDocument, useDocuments, useRemoveDocument, useUpdateClientPackage } from '../../api';
import type { DocumentStatus } from '../../api/generated/client';
import { DEFAULT_INPUT_DATE_FORMAT } from '../../config/constants';
import type { IDocument } from '../../interfaces/document';
import type { IClientPackageAppendixDocument, IDocumentTableData, IPackage } from '../../interfaces/package';
import { AgGridTable } from '../atoms';
import { ClientPackageAdditionalDocuments } from './ClientPackageAdditionalDocuments';
import { DocumentTableActionsCell } from './DocumentTableActionsCell';

// Title height and margin (64 px) + screen padding top/bottom (80 px)
const HEADER_TITLE_AND_PADDINGS_HEIGHT = 144;
const DETAIL_ROW_HEIGHT = 36;
const DETAIL_ACTION_ROW_HEIGHT_AND_MARGIN = 36 + 16;

const translationKeyMap: Record<DocumentStatus, string> = {
    DRAFT: 'clientPackageStatusDraft',
    FINALIZED: 'clientPackageStatusFinalised',
};

const zfStatusColorMap: Record<DocumentStatus, 'yellow' | 'green'> = {
    DRAFT: 'yellow',
    FINALIZED: 'green',
};

export const DocumentTable: FunctionComponent = () => {
    const { t } = useTranslation();

    const { sortedPackages } = useClientPackages();

    const updateClientPackage = useUpdateClientPackage();
    const deleteAppendixDocument = useDeleteAppendixDocument();
    const addGenericDocuments = useAddGenericDocuments();
    const removeDocument = useRemoveDocument();

    const { data: allDocuments } = useDocuments();

    const tableData: IDocumentTableData[] = useMemo(
        () =>
            sortedPackages.map((clientPackage) => ({
                id: clientPackage.id!,
                name: clientPackage.name,
                status: clientPackage.status,
                updatedBy: clientPackage.updatedBy,
                createdAt: clientPackage.createdAt,
                finalizedAt: clientPackage.finalizedAt,
                comment: clientPackage.comment,
                additionalDocuments: [...clientPackage.clientPackageAppendixDocuments]
                    .filter((appendix) => appendix.type === 'OTHER')
                    .sort((a, b) => a.documentId - b.documentId),
                clientPackageDocuments: clientPackage.clientPackageDocuments,
            })),
        [sortedPackages],
    );

    const tableDetailRowHeight = useMemo<number>(() => {
        const draftPackage = tableData.find(({ status }) => status === 'DRAFT');

        if (draftPackage) {
            return (
                draftPackage.additionalDocuments.length * DETAIL_ROW_HEIGHT +
                draftPackage.clientPackageDocuments.length * DETAIL_ROW_HEIGHT +
                DETAIL_ACTION_ROW_HEIGHT_AND_MARGIN
            );
        } else {
            return 0;
        }
    }, [tableData]);

    const handleUploadNewGenericDocuments = (packageId: IPackage['id'], files: File[]) => addGenericDocuments.mutateAsync({ packageId, files });
    const handleDeleteAdditionalDocument = (genericDocument: IClientPackageAppendixDocument) => deleteAppendixDocument.mutateAsync(genericDocument.documentId);
    const handleDeleteClientPackageDocument = (documentId: IDocument['id']) => removeDocument.mutateAsync(documentId);

    const columnsDefinition: ColDef<IDocumentTableData>[] = [
        {
            field: 'name',
            headerName: t('packageColHeaderName'),
            cellRenderer: 'agGroupCellRenderer',
            flex: 1,
            cellClass: ({ data }) => (data?.status === 'DRAFT' ? 'editable-cell' : ''),
            editable: ({ data }) => data?.status === 'DRAFT',
            onCellValueChanged: ({ colDef, data, newValue }) => {
                if (colDef.field === 'name') updateClientPackage.mutateAsync({ name: newValue, id: data.id });
            },
        },
        {
            field: 'status',
            headerName: t('packageColHeaderStatus'),
            maxWidth: 150,
            cellRenderer: ({ value }: ICellRendererParams<IDocumentTableData, DocumentStatus>) => (
                <ZfPill clickable={false} size="small" color={zfStatusColorMap[value!]}>
                    {t(translationKeyMap[value!])}
                </ZfPill>
            ),
        },
        {
            field: 'updatedBy',
            headerName: t('packageColHeaderModifiedBy'),
            width: 220,
        },
        {
            field: 'createdAt',
            headerName: t('packageColHeaderInitDate'),
            valueFormatter: ({ value }) => moment(value).format(DEFAULT_INPUT_DATE_FORMAT),
        },
        {
            field: 'finalizedAt',
            headerName: t('packageColHeaderFinaliseDate'),
            valueFormatter: ({ value }) => (value ? moment(value).format(DEFAULT_INPUT_DATE_FORMAT) : '-'),
        },
        {
            field: 'comment',
            headerName: t('packageColHeaderComment'),
            flex: 1,
            valueFormatter: ({ value }) => value || t('clientPackageNoComment'),
        },
        {
            headerName: t('packageColHeaderActions'),
            headerClass: 'ag-right-aligned-header',
            cellClass: 'ag-right-aligned-cell',
            sortable: false,
            cellRenderer: DocumentTableActionsCell,
        },
    ];

    return (
        <AgGridTable
            rowData={tableData}
            columnDefs={columnsDefinition}
            defaultColDef={{ resizable: false }}
            masterDetail={true}
            isRowMaster={(data) => data.status === 'DRAFT'}
            detailRowAutoHeight={true}
            onFirstDataRendered={(params) => {
                const interval = setInterval(() => {
                    const firstRow = params.api.getDisplayedRowAtIndex(0);
                    if (firstRow && !firstRow.expanded) {
                        firstRow.setExpanded(true);
                        clearInterval(interval);
                    }
                }, 100);
            }}
            detailCellRenderer={({ data }: IDetailCellRendererParams<IDocumentTableData>) =>
                data ? (
                    <ClientPackageAdditionalDocuments
                        packageId={data.id}
                        documents={allDocuments ?? []}
                        additionalDocuments={data.additionalDocuments}
                        clientPackageDocuments={data.clientPackageDocuments}
                        onUploadNewGenericDocuments={handleUploadNewGenericDocuments}
                        onDeleteAdditionalDocument={handleDeleteAdditionalDocument}
                        onRemoveClientPackageDocument={handleDeleteClientPackageDocument}
                    />
                ) : null
            }
            addTableContainer={true}
            screenHeaderPaddingsAndHeight={HEADER_TITLE_AND_PADDINGS_HEIGHT}
            tableDetailRowHeight={tableDetailRowHeight}
        />
    );
};
